export type DeckSegment = { text: string; changed: boolean };

const tokenize = (value: string): string[] =>
	value.match(/\s+|[^\s]+/g) ?? [];

export const diffTokens = (
	before: string,
	after: string,
): { before: DeckSegment[]; after: DeckSegment[] } => {
	const a = tokenize(before);
	const b = tokenize(after);

	if (a.length * b.length > 250000) {
		return {
			before: [{ text: before, changed: before !== after }],
			after: [{ text: after, changed: before !== after }],
		};
	}

	const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
		new Array<number>(b.length + 1).fill(0),
	);
	for (let i = a.length - 1; i >= 0; i -= 1) {
		for (let j = b.length - 1; j >= 0; j -= 1) {
			lcs[i][j] =
				a[i] === b[j]
					? lcs[i + 1][j + 1] + 1
					: Math.max(lcs[i + 1][j], lcs[i][j + 1]);
		}
	}

	const beforeSegments: DeckSegment[] = [];
	const afterSegments: DeckSegment[] = [];
	const push = (target: DeckSegment[], text: string, changed: boolean) => {
		const tail = target[target.length - 1];
		if (tail && tail.changed === changed) tail.text += text;
		else target.push({ text, changed });
	};

	let i = 0;
	let j = 0;
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			push(beforeSegments, a[i], false);
			push(afterSegments, b[j], false);
			i += 1;
			j += 1;
		} else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
			push(beforeSegments, a[i], true);
			i += 1;
		} else {
			push(afterSegments, b[j], true);
			j += 1;
		}
	}
	while (i < a.length) {
		push(beforeSegments, a[i], true);
		i += 1;
	}
	while (j < b.length) {
		push(afterSegments, b[j], true);
		j += 1;
	}

	return { before: beforeSegments, after: afterSegments };
};
